import { OpenAppParams } from 'app/shared/types/open-app-params.types';
import { isFresherToken, isSameSession } from 'app/shared/utils/jwt';
import qs from 'qs';

const LOCAL_STORAGE_KEY = 'openAppParams';

export const useOpenParams = (): OpenAppParams => {
  const routeParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const params = {
    authToken: routeParams.authToken as string || '',
    apiUrl: routeParams.apiUrl as string || '',
    locale: routeParams.locale as string || 'en',
    buildingUuid: routeParams.buildingUuid as string || '',
    appBrand: routeParams.appBrand as string || '',
  };
  // Guarded: a corrupted/tampered openAppParams entry must not throw on every render and
  // brick boot — fall back to the URL launch params instead.
  let stored: OpenAppParams | null = null;
  try {
    const storedParams = localStorage.getItem(LOCAL_STORAGE_KEY);
    stored = storedParams ? (JSON.parse(storedParams) as OpenAppParams) : null;
  } catch {
    stored = null;
  }

  const hasFullLaunchParams = Boolean(
    routeParams.authToken && routeParams.apiUrl && routeParams.appBrand && routeParams.buildingUuid && routeParams.locale,
  );

  if (hasFullLaunchParams) {
    // The webview is launched once with a token in the URL and is never re-navigated,
    // so on any reload (or the SDK's init-timeout location.replace) the query-param
    // token can be OLDER than one useTokenRefresh already refreshed and stored. Prefer the
    // stored token ONLY when it's the SAME session (sub + sessionUuid) AND genuinely
    // fresher — never combine a prior session's leftover token with this launch's context.
    const keepStored = Boolean(stored?.authToken)
      && isSameSession(stored?.authToken, params.authToken)
      && isFresherToken(stored?.authToken, params.authToken);
    const authToken = keepStored ? (stored as OpenAppParams).authToken : params.authToken;
    const resolved = { ...params, authToken };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resolved));

    return resolved;
  }

  return stored ?? params;
};

// Persist a refreshed token so a hard reload (which re-reads localStorage) uses it.
export const updateStoredAuthToken = (authToken: string): void => {
  try {
    const storedParams = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!storedParams) {
      return;
    }

    const parsed = JSON.parse(storedParams);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ ...parsed, authToken }));
  } catch {
    // Storage unavailable/malformed — in-memory token state is the source of truth.
  }
};
